import { DEFAULT_CURRENCY, DEFAULT_LOCALE, DEFAULT_MEASUREMENT } from "@utils/default";
import { mapLocaleCodeWithDefault } from "@utils/localeMapping/localeMapping";
import { toUnicodeLocale, trimObjectProperties } from "@utils/string";

import { HeroFirstCard } from "@/components/HeroCarousel/HeroFirstCard";
import { IntroInventory } from "@/components/InventoryLpIntro/type";
import {
  ArticleTypes,
  CommercetoolsCategory,
  CommercetoolsProject,
  CustomLink,
  Facets,
  ImageElement,
  Location,
  Media,
  PRODUCT_TYPE,
  Site,
  TargetSite,
  UnitAnchorsProps,
  VideoElement,
} from "@/types";

interface DamacAPIConfig {
  locale?: string;
  currency?: string;
  measurement?: string;
}

export interface Entry {
  id: string;
  type: string;
  internalName: string;
}

export interface SeoMetadata extends Entry {
  title?: string;
  description?: string;
  keywords?: string[];
  canonical?: string;
  noindex: boolean;
  nofollow: boolean;
  metaImage?: ImageElement;
}

interface basePage {
  internalName: string;
  type: string;
  slug: string;
  errors?: any;
  message?: string; // TODO move it inside errors (in the sdk repo)
  id: string;
}
export interface EditorialPage extends basePage {
  seoMetadata?: SeoMetadata;
  components: Entry[];
  componentsCount?: number;
  fixedHeader: boolean;
  stickyCta?: CustomLink;
  listingPages?: any;
  target?: TargetSite;
  listCardTitle?: string;
  cardDescription?: string;
  thumbnail?: ImageElement;
  pageType?: boolean;
  subPageType?: string;
  pageTypeData?: any;
}

export interface FiltrableArticleAttributes {
  years?: string[];
  tags?: string[];
  months?: string[];
}

export interface ProjectPage extends basePage {
  seoMetadata?: SeoMetadata;
  category: string;
  logo: Media;
  hero: HeroFirstCard;
  intro: IntroInventory;
  anchors: UnitAnchorsProps;
  components: Entry[];
  primaryFont: "Termina" | "El Messiri" | "Josefin Sans"; // TODO need to understand if that's the desired behaviour
  backgroundColor?: string;
  primaryColor?: string;
  accentColor?: string;
  projectData: CommercetoolsProject;
  projectStatus?: "Launch" | "Teaser" | "Post Launch" | "Sold Out" | "Reserved" | "Reveal";
  //TODO split to Property Type
  media?: Media[];
  projectSlug?: string;
  others?: string[];
  projectLogo?: Media;
  pixelStreaming?: boolean;
  pixelStreamingProjectId?: string;
  teaserTitle?: string;
  teaserDescription?: string;
  revealTitle?: string;
  revealDescription?: string;
  soldOutTitle?: string;
  soldOutDescription?: string;
  communitySlug?: string;
  goldenVisa?: boolean;
  exclusive?: boolean;
}

interface Article extends basePage {
  title: string;
  postDate: string;
  tags: string[];
  summary: string;
  body: string;
  components: Entry[];
  image: ImageElement;
  video: VideoElement;
  cta: CustomLink;
  backgroundTags?: string[];
  articleType: ArticleTypes;
  seoMetadata?: SeoMetadata;
}

export interface CommunityPage extends basePage {
  seoMetadata?: SeoMetadata;
  primaryFont: "Termina" | "El Messiri" | "Josefin Sans";
  backgroundColor?: string;
  primaryColor?: string;
  accentColor?: string;
  hero: HeroFirstCard;
  intro: IntroInventory;
  components: Entry[];
  communityData: CommunityData;
  communityName: string;
  category: string;
  fallbackTitle?: string;
  fallbackDescription?: string;
}

interface CommunityData {
  minPrice?: number;
  minBedrooms?: number;
  maxBedrooms?: number;
  propertyTypes?: string[];
}

export interface ComponentFilters {
  [key: string]: {
    value: string;
    operator?: string;
  }[];
}

export interface CatalogResponse {
  results: CommercetoolsProject[];
  filters: Facets;
  locations: Location[];
  count: number;
  total: number;
}

export interface PaymentTermRecord {
  Description__c: string;
  Id: string;
  Installment__c: string;
  Milestone_Event__c: string;
  Payment_Plan__c: string;
  Percentage__c: number;
  attributes: {
    type: string;
    url: string;
  };
}

interface PaymentTerms {
  done: boolean;
  records: PaymentTermRecord[];
  totalSize: number;
}

export interface PaymentPlanProps {
  paymentPlan: {
    Id: string;
    Name: string;
    Payment_Term_Description__c: string;
    Payment_Terms__r: PaymentTerms;
    attributes: {
      type: string;
      url: string;
    };
  };
}

type PageType = "pageProject" | "pageEditorial" | "article" | "pagePropertyType" | "pageCommunity";

type DataReturnType<T extends PageType> = T extends "pageProject" | "pagePropertyType"
  ? ProjectPage
  : T extends "article"
    ? Article
    : T extends "pageEditorial"
      ? EditorialPage
      : T extends "pageCommunity"
        ? CommunityPage
        : CommunityPage;

export class DamacAPI {
  private _locale: string;
  private _baseURL: URL;
  private _currency: string;
  private _measurement: string;
  private _limit: number;

  constructor(config: DamacAPIConfig) {
    const { locale, currency, measurement } = config;
    const mappedLocale = mapLocaleCodeWithDefault(locale || DEFAULT_LOCALE);
    this._locale = toUnicodeLocale(mappedLocale || DEFAULT_LOCALE);
    this._baseURL = new URL(`${process.env.AW_BASE_URL}`);
    this._baseURL.searchParams.append("locale", this._locale);
    this._currency = currency || DEFAULT_CURRENCY;
    this._measurement = measurement || DEFAULT_MEASUREMENT;
    this._limit = 10;
  }

  public async componentByType(
    type: string,
    preview: boolean = false,
    order?: string,
    limit?: number,
    skip?: number,
    filters?: ComponentFilters,
    cache: boolean = false,
  ) {
    try {
      const getComponentUrl = new URL(this._baseURL.toString());
      getComponentUrl.pathname += `content/component`;
      getComponentUrl.searchParams.append("type", type);
      getComponentUrl.searchParams.append("preview", `${preview}`);
      if (order) {
        getComponentUrl.searchParams.append("orderBy", order);
      }
      if (limit && limit > 0) {
        getComponentUrl.searchParams.append("limit", limit.toString());
      }
      if (skip && skip > 0) {
        getComponentUrl.searchParams.append("skip", skip.toString());
      }
      if (filters) {
        getComponentUrl.searchParams.append("filters", JSON.stringify(filters));
      }
      const contentfulDataRequest = await fetch(getComponentUrl, {
        headers: {
          "x-api-key": `${process.env.AW_API_KEY}`,
        },
        next: {
          ...(cache ? { tags: ["component"], revalidate: 3600 } : { revalidate: 0 }),
          // tags: ["component"],
        },
      });
      const componentData = await contentfulDataRequest.json();
      return componentData;
    } catch (e) {
      console.error(e);
    }
  }
  public async getSEOMeta(name: string) {
    try {
      const getComponentUrl = new URL(this._baseURL.toString());
      getComponentUrl.pathname += `content/component`;
      getComponentUrl.searchParams.append("type", "seoMetadata");
      getComponentUrl.searchParams.append("filters", JSON.stringify({ internalName: [{ value: name }] }));
      const contentfulDataRequest = await fetch(getComponentUrl, {
        headers: {
          "x-api-key": `${process.env.AW_API_KEY}`,
        },
        next: {
          tags: ["component"],
        },
      });
      const componentData = await contentfulDataRequest.json();
      return componentData;
    } catch (e) {
      console.error(e);
    }
  }

  public async getProjectAvailability(cmsProjId: any) {
    try {
      const requestUrl = new URL(this._baseURL.toString());
      requestUrl.pathname += "catalog/availability-project";
      const response = await fetch(requestUrl, {
        method: "POST",
        body: JSON.stringify({ cmsProjId }),
        headers: {
          "x-api-key": `${process.env.AW_API_KEY}`,
          "Content-Type": "application/json",
        },
      });
      return await response.json();
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  public async pageBySlug<T extends PageType>(slug: string, preview: boolean = false, pageType: T): Promise<DataReturnType<T> | undefined> {
    try {
      const pageUrl = new URL(this._baseURL.toString());
      pageUrl.pathname += `content/page`;
      pageUrl.searchParams.append("slug", slug);
      if (pageType) {
        pageUrl.searchParams.append("pageType", pageType);
      }
      pageUrl.searchParams.append("preview", `${preview}`);
      pageUrl.searchParams.append("currency", this._currency);
      pageUrl.searchParams.append("measurement", this._measurement);
      // console.log(`===============${slug} url=======================:`, pageUrl.toString());
      const contentfulDataRequest = await fetch(pageUrl, {
        headers: {
          "x-api-key": `${process.env.AW_API_KEY}`,
        },
        next: {
          tags: ["page"],
          revalidate: 3600,
        },
      });

      let data = await contentfulDataRequest.json();
      // console.log(`=======================${slug} response:============================================`, data);
      if (Object.keys(data).length == 0) {
        return;
      } else {
        return data as DataReturnType<T>;
      }
    } catch (e) {
      console.error(e);
    }
  }

  public async getPageTypeData(subPageType: string, preview: boolean = false): Promise<any> {
    try {
      const pageUrl = new URL(this._baseURL.toString());
      if (!pageUrl.pathname.endsWith("/")) pageUrl.pathname += "/";
      pageUrl.pathname += `content/page-type`;
      pageUrl.searchParams.append("pageType", "pageEditorial");
      pageUrl.searchParams.append("preview", `${preview}`);
      pageUrl.searchParams.append("currency", this._currency);
      pageUrl.searchParams.append("measurement", this._measurement);
      const finalUrl = `${pageUrl.toString()}&subPageType=${encodeURIComponent(subPageType).replace(/%20/g, "%20")}`;
      const response = await fetch(finalUrl, {
        headers: { "x-api-key": `${process.env.AW_API_KEY}` },
        next: { tags: ["page-type"], revalidate: 3600 },
      });
      const data = await response.json();
      return Object.keys(data).length > 0 ? data : undefined;
    } catch (e) {
      console.error(e);
    }
  }

  public async assetByTitle(title: string) {
    try {
      const assetUrl = new URL(this._baseURL.toString());
      assetUrl.pathname += `content/asset`;
      assetUrl.searchParams.append("title", title);
      const contentfulDataRequest = await fetch(assetUrl, {
        headers: {
          "x-api-key": `${process.env.AW_API_KEY}`,
        },
        next: {
          tags: ["asset"],
        },
      });

      let data = await contentfulDataRequest.json();

      if (Object.keys(data).length == 0) {
        return;
      } else {
        return data;
      }
    } catch (e) {
      console.error(e);
    }
  }

  public async getTranslations() {
    try {
      const pageUrl = new URL(this._baseURL.toString());
      pageUrl.pathname += `content/translations`;
      const contentfulDataRequest = await fetch(pageUrl, {
        headers: {
          "x-api-key": `${process.env.AW_API_KEY}`,
        },
        next: {
          tags: ["translations"],
          revalidate: 3600,
        },
      });
      const translations: Record<string, any> = await contentfulDataRequest.json();
      return translations;
    } catch (e) {
      console.error(e);
    }
  }
  public async submitNewsletter(data = {}) {
    try {
      const requestUrl = new URL(this._baseURL.toString());
      requestUrl.pathname += `user/newsletter`;
      const parsedData: Record<string, any> = { ...data };
      parsedData.locale = toUnicodeLocale(parsedData?.locale ?? DEFAULT_LOCALE);
      const response = await fetch(requestUrl, {
        method: "POST",
        body: JSON.stringify(trimObjectProperties(parsedData)),
        headers: {
          "x-api-key": `${process.env.AW_API_KEY}`,
        },
      });
      return response.json();
    } catch (e) {
      console.error(e);
    }
  }

  public async checkout(
    data: Record<string, any>,
    type:
      | "leadCeation"
      | "sendOTP"
      | "otpVerify"
      | "leadVerify"
      | "paymentSession"
      | "paymentMethods"
      | "paymentSubmit"
      | "paymentConfirmation"
      | "userCreation"
      | "paymentDetails",
  ) {
    // console.log(`checkout ${type}`, data);
    try {
      let endpoint: string;
      switch (type) {
        case "leadCeation":
          endpoint = `checkout/lead-creation`;
          break;
        case "sendOTP":
          endpoint = `checkout/send-otp`;
          break;
        case "otpVerify":
          endpoint = `checkout/otp-verify`;
          break;
        case "leadVerify":
          endpoint = `checkout/lead-verify`;
          break;
        case "paymentSession":
          endpoint = `checkout/payment-session`;
          break;
        case "paymentMethods":
          endpoint = `checkout/payment-methods`;
          break;
        case "paymentConfirmation":
          endpoint = `checkout/payment-confirmation`;
          break;
        case "paymentSubmit":
          endpoint = `checkout/payment-submit`;
          break;
        case "paymentDetails":
          endpoint = `checkout/payment-details`;
          break;
        case "userCreation":
          endpoint = `checkout/user-creation`;
          break;
        default:
          throw new Error("Invalid type");
      }
      const requestUrl = new URL(this._baseURL.toString());
      requestUrl.pathname += endpoint;
      const parsedData: Record<string, any> = { ...data };
      parsedData.locale = toUnicodeLocale(parsedData?.locale ?? DEFAULT_LOCALE);
      const request = await fetch(requestUrl, {
        method: "POST",
        body: JSON.stringify(trimObjectProperties(parsedData)),
        headers: {
          "x-api-key": `${process.env.AW_API_KEY}`,
        },
      });
      const response = await request.json();
      // console.log(`checkout response`, response);
      return response;
    } catch (e) {
      console.error(e);
    }
  }

  public async downloadReceipt(receiptId: string) {
    const requestUrl = new URL(this._baseURL.toString());
    requestUrl.pathname += `checkout/receipt`;
    requestUrl.searchParams.append("receiptId", receiptId);
    const response = await fetch(requestUrl, {
      headers: {
        "x-api-key": `${process.env.AW_API_KEY}`,
        "Content-Type": "application/pdf",
      },
      next: {
        tags: ["receipt"],
      },
    });

    return response;
  }

  public async sendReceipt(orderID: string): Promise<boolean> {
    const requestUrl = new URL(this._baseURL.toString());
    requestUrl.pathname += `checkout/send-receipt/`;
    requestUrl.searchParams.append("orderID", orderID);
    const response = await fetch(requestUrl, {
      headers: {
        "x-api-key": `${process.env.AW_API_KEY}`,
        "Content-Type": "application/json",
      },
      next: {
        tags: ["send-receipt"],
      },
    });
    const rawResponse = await response.json();
    return rawResponse.success;
  }

  public async getOrder(orderId: string): Promise<Record<string, any> | boolean> {
    try {
      if (!orderId) {
        throw "Missing order id";
      }
      const pageUrl = new URL(this._baseURL.toString());
      pageUrl.pathname += `checkout/order`;
      pageUrl.searchParams.append("orderId", orderId);
      const request = await fetch(pageUrl, {
        headers: {
          "x-api-key": `${process.env.AW_API_KEY}`,
        },
        next: {
          tags: ["similar-projects"],
          revalidate: 3600,
        },
      });
      const order = await request.json();
      return order;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  /**
   * This function retrieve a list of projects for commercetools,
   * by default sorted by most recent launch date
   * @param categoryId exclude the specific project
   * @param cityId limit to same city
   * @param minPrice set a min price for the search
   * @param maxPrice set a max price for the search
   * @param limit set a limit for the search
   * @returns projects list
   */
  public async getSimilarProjects(
    categoryId?: string,
    cityId?: string,
    minPrice?: number,
    maxPrice?: number,
    limit = this._limit,
  ): Promise<CommercetoolsProject[]> {
    try {
      const pageUrl = new URL(this._baseURL.toString());
      pageUrl.pathname += `catalog/similar-project`;
      if (categoryId) {
        pageUrl.searchParams.append("category", categoryId);
      }
      if (cityId) {
        pageUrl.searchParams.append("city", cityId);
      }
      if (minPrice) {
        pageUrl.searchParams.append("minPrice", `${minPrice}`);
      }
      if (maxPrice) {
        pageUrl.searchParams.append("maxPrice", `${maxPrice}`);
      }
      pageUrl.searchParams.append("limit", `${limit}`);
      pageUrl.searchParams.append("currency", this._currency);
      pageUrl.searchParams.append("measurement", this._measurement);
      const request = await fetch(pageUrl, {
        headers: {
          "x-api-key": `${process.env.AW_API_KEY}`,
        },
        next: {
          tags: ["similar-projects"],
          revalidate: 3600,
        },
      });
      const similarProjects: Record<string, any> = await request.json();
      if (Object.keys(similarProjects).length > 0) {
        return similarProjects.result;
      }
      return [];
    } catch (e) {
      console.error(e);
      return [];
    }
  }

  /**
   * This function retrieve a list of projects for commercetools,
   * by default sorted by most recent launch date
   * @param community
   * @param limit set a limit for the search
   * @returns projects list
   */
  public async getCommunityProjects(community?: string, limit = this._limit): Promise<CommercetoolsProject[]> {
    try {
      const pageUrl = new URL(this._baseURL.toString());
      pageUrl.pathname += `catalog/community-project`;
      if (community) {
        pageUrl.searchParams.append("community", community);
      }
      pageUrl.searchParams.append("limit", `${limit}`);
      pageUrl.searchParams.append("currency", this._currency);
      pageUrl.searchParams.append("measurement", this._measurement);
      const request = await fetch(pageUrl, {
        headers: {
          "x-api-key": `${process.env.AW_API_KEY}`,
        },
        next: {
          tags: ["community-projects"],
          revalidate: 3600,
        },
      });
      const communityProjects: Record<string, any> = await request.json();
      if (Object.keys(communityProjects).length > 0) {
        return communityProjects.result;
      }
      return [];
    } catch (e) {
      console.error(e);
      return [];
    }
  }

  public async submitLoginSendOtp(data = {}): Promise<any> {
    try {
      const requestUrl = new URL(this._baseURL.toString());
      requestUrl.pathname += "user/login/send-otp";
      const response = await fetch(requestUrl, {
        method: "POST",
        body: JSON.stringify(data),
        headers: {
          "x-api-key": `${process.env.AW_API_KEY}`,
        },
      });
      const json = await response.json();
      return json;
    } catch (e) {
      console.error(e);
    }
  }
  public async submitLoginVerifyOtp(data = {}): Promise<any> {
    try {
      const requestUrl = new URL(this._baseURL.toString());
      requestUrl.pathname += "user/login/verify-otp";
      const response = await fetch(requestUrl, {
        method: "POST",
        body: JSON.stringify(data),
        headers: {
          "x-api-key": `${process.env.AW_API_KEY}`,
        },
      });
      const json = await response.json();
      return !json.error ? json : null;
    } catch (e) {
      console.error(e);
    }
  }
  public async submitLoginCredentials(data = {}): Promise<any> {
    try {
      const requestUrl = new URL(this._baseURL.toString());
      requestUrl.pathname += "user/login/credentials";
      const response = await fetch(requestUrl, {
        method: "POST",
        body: JSON.stringify(data),
        headers: {
          "x-api-key": `${process.env.AW_API_KEY}`,
        },
      });
      const json = await response.json();
      // console.info("submitLoginCredentials", requestUrl, data, json);
      return !json.error ? json : null;
    } catch (e) {
      console.error(e);
    }
  }
  public async submitLoginSocial(data = {}): Promise<any> {
    try {
      const requestUrl = new URL(this._baseURL.toString());
      requestUrl.pathname += "user/login/social";
      const response = await fetch(requestUrl, {
        method: "POST",
        body: JSON.stringify(data),
        headers: {
          "x-api-key": `${process.env.AW_API_KEY}`,
        },
      });
      const json = await response.json();
      // console.info("submitLoginSocial", requestUrl, data, json);
      return json;
    } catch (e) {
      console.error(e);
    }
  }

  // public async submitLoginWithDamac(data = {}): Promise<any> {
  //   try {
  //     const requestUrl = new URL(this._baseURL.toString());
  //     requestUrl.pathname += "user/login/credentials";
  //     const response = await fetch(requestUrl, {
  //       method: "POST",
  //       body: JSON.stringify(data),
  //       headers: {
  //         "x-api-key": `${process.env.AW_API_KEY}`,
  //       },
  //     });
  //     return response.json();
  //   } catch (e) {
  //     console.error(e);
  //   }
  // }
  /**
   * This function retrieve a list of categories for commercetools by ID,
   * @param IDs specify requested categories
   * @returns projects list
   */
  public async getCategoriesByIDs(IDs: string[]): Promise<CommercetoolsCategory[]> {
    try {
      const pageUrl = new URL(this._baseURL.toString());
      pageUrl.pathname += `catalog/categories`;
      pageUrl.searchParams.append("IDs", IDs.toString());

      const request = await fetch(pageUrl, {
        headers: {
          "x-api-key": `${process.env.AW_API_KEY}`,
        },
        next: {
          tags: ["projects"],
          revalidate: 3600,
        },
      });
      const projects: Record<string, any> = await request.json();
      if (Object.keys(projects).length > 0) {
        return projects.results;
      }
      return [];
    } catch (e) {
      console.error(e);
      return [];
    }
  }
  /**
   * This function retrieve a list of filtrable attributes for articles,
   */
  public async getArticleFilters(locale: string, articleType: ArticleTypes): Promise<FiltrableArticleAttributes> {
    try {
      const pageUrl = new URL(this._baseURL.toString());
      pageUrl.pathname += `content/article-filters`;
      pageUrl.searchParams.append("locale", locale);
      if (articleType) {
        pageUrl.searchParams.append("articleType", articleType);
      }
      pageUrl.searchParams.append("site", TargetSite[process.env.SITE_CURRENT as Site]);

      const request = await fetch(pageUrl, {
        headers: {
          "x-api-key": `${process.env.AW_API_KEY}`,
        },
        next: {
          tags: ["article-filters"],
          revalidate: 3600,
        },
      });
      const jsonResponse = await request.json();
      if (Object.keys(jsonResponse).length > 0) {
        return jsonResponse;
      }
      return {};
    } catch (e) {
      console.error(e);
      return {};
    }
  }
  public async getUnits(params: Record<string, any>) {
    try {
      const pageUrl = new URL(this._baseURL.toString());
      pageUrl.pathname += `catalog/units`;
      Object.keys(params).forEach((key) => {
        pageUrl.searchParams.append(key, params[key]);
      });
      pageUrl.searchParams.append("currency", this._currency);
      pageUrl.searchParams.append("measurement", this._measurement);
      pageUrl.searchParams.append("sort", "variants.attributes.priceAED asc");
      const request = await fetch(pageUrl, {
        headers: {
          "x-api-key": `${process.env.AW_API_KEY}`,
        },
        next: {
          tags: ["units"],
          revalidate: 3600,
        },
      });
      const jsonResponse = await request.json();
      // console.log("getUnits jsonResponse", jsonResponse);
      if (Object.keys(jsonResponse).length > 0) {
        return jsonResponse;
      }
      return {};
    } catch (e) {
      console.error(e);
      return {};
    }
  }
  public async profile(customerId: string) {
    // console.info("profile customerId", customerId);
    try {
      const url = new URL(this._baseURL.toString());
      url.pathname += "user/profile";
      url.searchParams.append("customerId", customerId);
      const response = await fetch(url, {
        headers: {
          "x-api-key": `${process.env.AW_API_KEY}`,
        },
      });
      const data = await response.json();
      // console.info("profile data", data);
      return data;
    } catch (e) {
      console.error(e);
      return {};
    }
  }
  public async search(params: Record<string, any>): Promise<CatalogResponse> {
    const pageUrl = new URL(this._baseURL.toString());
    pageUrl.pathname += `catalog`;
    if (params?.locale) {
      pageUrl.searchParams.delete("locale");
    }
    Object.keys(params).forEach((key) => {
      pageUrl.searchParams.append(key, params[key]);
    });
    pageUrl.searchParams.append("currency", this._currency);
    pageUrl.searchParams.append("measurement", this._measurement);
    // if (!params?.limit) {
    //   pageUrl.searchParams.append("limit", "12");
    // }
    if (!params?.type) {
      pageUrl.searchParams.append("type", PRODUCT_TYPE.PROPERTY_TYPE);
    }

    const request = await fetch(pageUrl, {
      headers: {
        "x-api-key": `${process.env.AW_API_KEY}`,
      },
      cache: "no-store",
      next: {
        tags: ["search"],
      },
    });
    const jsonResponse = await request.json();
    if (Object.keys(jsonResponse).length > 0 && (!jsonResponse.error || Object.keys(jsonResponse.error).length === 0)) {
      return jsonResponse;
    }
    throw Error(JSON.stringify(jsonResponse, null, 2));
  }
  public async productInfo(params: Record<string, any>): Promise<CommercetoolsProject> {
    const pageUrl = new URL(this._baseURL.toString());
    pageUrl.pathname += `catalog/info`;
    Object.keys(params).forEach((key) => {
      pageUrl.searchParams.append(key, params[key]);
    });
    pageUrl.searchParams.append("currency", this._currency);
    pageUrl.searchParams.append("measurement", this._measurement);
    if (!params.locale) {
      pageUrl.searchParams.append("locale", DEFAULT_LOCALE);
    }
    const request = await fetch(pageUrl, {
      headers: {
        "x-api-key": `${process.env.AW_API_KEY}`,
      },
      // next: {
      //   tags: ["search"],
      //   revalidate: 3600,
      // },
      cache: "no-cache",
    });
    const jsonResponse = await request.json();
    if (Object.keys(jsonResponse).length > 0 && !jsonResponse.error) {
      return jsonResponse;
    }
    throw Error(JSON.stringify(jsonResponse, null, 2));
  }

  public async paymentPlan(inventoryId: string): Promise<PaymentPlanProps> {
    try {
      const pageUrl = new URL(this._baseURL.toString());
      pageUrl.pathname += `catalog/payment-plan`;
      pageUrl.searchParams.append("inventoryId", inventoryId);

      const request = await fetch(pageUrl, {
        headers: {
          "x-api-key": `${process.env.AW_API_KEY}`,
        },
      });
      const jsonResponse = await request.json();
      if (Object.keys(jsonResponse).length > 0 && !jsonResponse.error) {
        return jsonResponse;
      }
      console.error(`Payment plan error for inventoryId ${inventoryId}:`, jsonResponse);
      throw Error(JSON.stringify(jsonResponse, null, 2));
    } catch (e) {
      console.error(`Error fetching payment plan for inventoryId ${inventoryId}:`, e);
      throw e;
    }
  }

  public async getPropertyTypePage(slug: string, projectSlug: string): Promise<Partial<ProjectPage>> {
    try {
      const pageUrl = new URL(this._baseURL.toString());
      pageUrl.pathname += `content/property-type`;
      pageUrl.searchParams.append("slug", slug);
      pageUrl.searchParams.append("projectSlug", projectSlug);
      pageUrl.searchParams.append("currency", this._currency);
      pageUrl.searchParams.append("measurement", this._measurement);
      const contentfulDataRequest = await fetch(pageUrl, {
        headers: {
          "x-api-key": `${process.env.AW_API_KEY}`,
        },
        next: {
          tags: ["page"],
        },
      });
      const data = await contentfulDataRequest.json();
      if (Object.keys(data).length == 0) {
        return {};
      } else {
        return data;
      }
    } catch (e) {
      console.error(e);
      return {};
    }
  }
  public async addToShortlist(data = {}) {
    try {
      const requestUrl = new URL(this._baseURL.toString());
      requestUrl.pathname += "user/shortlist/add";
      // console.info("addToShortlist requestUrl", requestUrl);
      const body = JSON.stringify({ locale: this._locale, currency: this._currency, measurement: this._measurement, ...data });
      // console.info("addToShortlist body", body);
      const response = await fetch(requestUrl, {
        method: "POST",
        body,
        headers: {
          "x-api-key": `${process.env.AW_API_KEY}`,
        },
      });
      const json = await response.json();
      // console.info("addToShortlist json", json);
      return json;
    } catch (e) {
      console.error(e);
    }
  }
  public async removeFromShortlist(data = {}) {
    try {
      // console.log("removeFromShortlist data", data);
      const requestUrl = new URL(this._baseURL.toString());
      requestUrl.pathname += "user/shortlist/remove";
      // console.log("removeFromShortlist requestUrl", requestUrl);
      const body = JSON.stringify({ locale: this._locale, currency: this._currency, measurement: this._measurement, ...data });
      // console.log("removeFromShortlist body", body);
      const response = await fetch(requestUrl, {
        method: "POST",
        body,
        headers: {
          "x-api-key": `${process.env.AW_API_KEY}`,
        },
      });
      const json = await response.json();
      // console.log("removeFromShortlist json", json);
      return json;
    } catch (e) {
      console.error(e);
    }
  }
  public async removeAllFromShortlist(data = {}) {
    try {
      const requestUrl = new URL(this._baseURL.toString());
      requestUrl.pathname += "user/shortlist/delete";
      // console.info("removeAllFromShortlist requestUrl", requestUrl);
      const body = JSON.stringify({ locale: this._locale, currency: this._currency, measurement: this._measurement, ...data });
      // console.info("removeAllFromShortlist body", body);
      const response = await fetch(requestUrl, {
        method: "POST",
        body,
        headers: {
          "x-api-key": `${process.env.AW_API_KEY}`,
        },
      });
      const json = await response.json();
      // console.info("removeAllFromShortlist json", json);
      return json;
    } catch (e) {
      console.error(e);
    }
  }
  public async shortlist(customerId?: string, productIds?: string[]) {
    try {
      const url = new URL(this._baseURL.toString());
      url.pathname += "user/shortlist";
      url.searchParams.append("currency", this._currency);
      url.searchParams.append("measurement", this._measurement);
      if (customerId) {
        url.searchParams.append("customerId", customerId);
      }
      if (productIds?.length) {
        url.searchParams.append("productIds", productIds.join(" "));
      }
      // console.log("shortlist url", url);
      const response = await fetch(url, {
        headers: {
          "x-api-key": `${process.env.AW_API_KEY}`,
        },
      });
      const json = await response.json();
      // console.log("shortlist json", json);
      return json;
    } catch (e) {
      console.error(e);
    }
  }
  public async mergeShortlist(data = {}) {
    try {
      const requestUrl = new URL(this._baseURL.toString());
      requestUrl.pathname += "user/shortlist/merge";
      // console.info("mergeShortlist requestUrl", requestUrl);
      const body = JSON.stringify({ locale: this._locale, currency: this._currency, measurement: this._measurement, ...data });
      // console.info("mergeShortlist body", body);
      const response = await fetch(requestUrl, {
        method: "POST",
        body,
        headers: {
          "x-api-key": `${process.env.AW_API_KEY}`,
        },
      });
      const json = await response.json();
      // console.info("mergeShortlist json", json);
      return json;
    } catch (e) {
      console.error(e);
    }
  }
  public async compare(customerId: string) {
    try {
      const url = new URL(this._baseURL.toString());
      url.pathname += "user/shortlist/compare";
      url.searchParams.append("customerId", customerId);
      const response = await fetch(url, {
        headers: {
          "x-api-key": `${process.env.AW_API_KEY}`,
        },
      });
      const data = await response.json();
      return data;
    } catch (e) {
      console.error(e);
    }
  }

  public async submitLead(data: Record<string, any>) {
    console.log("submitLead data", data);
    try {
      console.log("submitLead data", data);
      const requestUrl = new URL(this._baseURL.toString());
      requestUrl.pathname += "user/lead";
      const response = await fetch(requestUrl, {
        method: "POST",
        body: JSON.stringify(trimObjectProperties(data)),
        headers: {
          "x-api-key": `${process.env.AW_API_KEY}`,
        },
      });
      const result = await response.json();
      return result;
    } catch (e) {
      console.error(e);
    }
  }

  public async submitGenericRequest(data = {}) {
    try {
      const requestUrl = new URL(this._baseURL.toString());
      requestUrl.pathname += "user/generic-request";
      const response = await fetch(requestUrl, {
        method: "POST",
        body: JSON.stringify(trimObjectProperties(data)),
        headers: {
          "x-api-key": `${process.env.AW_API_KEY}`,
        },
      });
      const result = await response.json();
      return result;
    } catch (e) {
      console.error(e);
    }
  }

  public async availability(data = {}) {
    try {
      // console.log("availability data", data);
      const requestUrl = new URL(this._baseURL.toString());
      requestUrl.pathname += "catalog/availability";
      const response = await fetch(requestUrl, {
        method: "POST",
        body: JSON.stringify(data),
        headers: {
          "x-api-key": `${process.env.AW_API_KEY}`,
        },
      });
      const result = await response.json();
      // console.log("availability result", result);
      return result;
    } catch (e) {
      console.error(e);
    }
  }

  public async getMetaverseSlots(projectID: string, date: string, time: string) {
    try {
      const url = new URL(this._baseURL.toString());
      url.pathname += "metaverse/slots";
      url.searchParams.append("projectID", projectID);
      url.searchParams.append("date", date);
      url.searchParams.append("time", time);

      const response = await fetch(url, {
        headers: { "x-api-key": process.env.AW_API_KEY ?? "" },
      });
      const result = await response.json();
      // console.info("getMetaverseSlots result", result);
      return result;
    } catch (e) {
      console.error(e);
      return {};
    }
  }

  public async submitMetaverseSession(data = {}) {
    try {
      // console.info("submitMetaverseSession data", data);
      const url = new URL(this._baseURL.toString());
      url.pathname += "metaverse/session";
      const response = await fetch(url, {
        method: "POST",
        body: JSON.stringify(data),
        headers: {
          "x-api-key": process.env.AW_API_KEY ?? "",
        },
      });
      // console.info("submitMetaverseSession response", response.status);
      const result = await response.json();
      // console.info("submitMetaverseSession result", result);
      return result;
    } catch (e) {
      console.error(e);
    }
  }

  public async submitSettings(data = {}) {
    try {
      const requestUrl = new URL(this._baseURL.toString());
      requestUrl.pathname += `user/settings`;
      const response = await fetch(requestUrl, {
        method: "POST",
        body: JSON.stringify(data),
        headers: {
          "x-api-key": `${process.env.AW_API_KEY}`,
        },
      });
      return response.json();
    } catch (e) {
      console.error(e);
    }
  }
  public async properties(customerId: string) {
    try {
      const url = new URL(this._baseURL.toString());
      url.pathname += "user/properties";
      url.searchParams.append("customerId", customerId);
      const response = await fetch(url, {
        headers: {
          "x-api-key": `${process.env.AW_API_KEY}`,
        },
      });
      const data = await response.json();
      return data;
    } catch (e) {
      console.error(e);
    }
  }
  public async reservationsPrebookings(customerId: string) {
    try {
      const url = new URL(this._baseURL.toString());
      url.pathname += "user/reservations-prebookings";
      url.searchParams.append("currency", this._currency);
      url.searchParams.append("measurement", this._measurement);
      url.searchParams.append("customerId", customerId);
      // console.info("reservationsPrebookings url", url);
      const response = await fetch(url, {
        headers: {
          "x-api-key": `${process.env.AW_API_KEY}`,
        },
      });
      const json = await response.json();
      // console.info("reservationsPrebookings json", json);
      return json;
    } catch (e) {
      console.error(e);
    }
  }
  public async appointments(customerId: string) {
    try {
      const url = new URL(this._baseURL.toString());
      url.pathname += "user/appointments";
      url.searchParams.append("customerId", customerId);
      const response = await fetch(url, {
        headers: {
          "x-api-key": `${process.env.AW_API_KEY}`,
        },
      });
      const data = await response.json();
      return data;
    } catch (e) {
      console.error(e);
    }
  }
  public async conversionRate(currency: string): Promise<number> {
    try {
      const url = new URL(this._baseURL.toString());
      url.pathname += "catalog/currency";
      url.searchParams.append("currency", currency);
      const response = await fetch(url, {
        next: { revalidate: 86400 }, // Revalidate every 24 hours
        headers: {
          "x-api-key": `${process.env.AW_API_KEY}`,
        },
      });
      const data = await response.json();
      return data;
    } catch (e) {
      console.error(e);
      return 1;
    }
  }
  public async entryById(id: string, preview: boolean = false) {
    try {
      const pageUrl = new URL(this._baseURL.toString());
      pageUrl.pathname += `content/entry`;
      pageUrl.searchParams.append("id", id);
      pageUrl.searchParams.append("preview", `${preview}`);
      const contentfulDataRequest = await fetch(pageUrl, {
        headers: {
          "x-api-key": `${process.env.AW_API_KEY}`,
        },
        next: {
          tags: ["entry"],
        },
      });

      let data = await contentfulDataRequest.json();

      return data;
    } catch (e) {
      console.error(e);
    }
  }
  public async pin() {
    try {
      const pageUrl = new URL(this._baseURL.toString());
      pageUrl.pathname += `catalog/pin`;
      const pinDataRequest = await fetch(pageUrl, {
        headers: {
          "x-api-key": `${process.env.AW_API_KEY}`,
        },
        next: {
          tags: ["mapPins"],
        },
      });
      let data = await pinDataRequest.json();
      return data;
    } catch (e) {
      console.error(e);
    }
  }

  public async fullCatalog(type: string = PRODUCT_TYPE.PROPERTY_TYPE) {
    try {
      const pageUrl = new URL(this._baseURL.toString());
      pageUrl.pathname += `catalog/full-catalog`;
      pageUrl.searchParams.append("type", type);
      const pinDataRequest = await fetch(pageUrl, {
        headers: {
          "x-api-key": `${process.env.AW_API_KEY}`,
        },
        next: {
          tags: ["fullCatalog"],
          revalidate: 900,
        },
      });
      let data = await pinDataRequest.json();
      return data;
    } catch (e) {
      console.error(e);
    }
  }

  public async submitGuidedTool(email: string, data: any[], lqsID: string) {
    try {
      // console.info("submitGuidedTool email", email);
      // console.info("submitGuidedTool data", data);
      // console.info("submitGuidedTool lqsID", lqsID);
      const requestUrl = new URL(this._baseURL.toString());
      requestUrl.pathname += "user/guided-tool";
      const response = await fetch(requestUrl, {
        method: "POST",
        body: JSON.stringify({ email, data, lqsID }),
        headers: {
          "x-api-key": `${process.env.AW_API_KEY}`,
        },
      });
      const result = await response.json();
      // console.info("submitGuidedTool result", result);
      return result;
    } catch (e) {
      console.error(e);
    }
  }

  public async monitor() {
    try {
      const pageUrl = new URL(this._baseURL.toString());
      pageUrl.pathname += `catalog/monitor`;
      const monitorRequest = await fetch(pageUrl, {
        headers: {
          "x-api-key": `${process.env.AW_API_KEY}`,
        },
        cache: "no-cache",
        next: {
          tags: ["monitor"],
        },
      });
      let data = await monitorRequest.json();
      return data;
    } catch (e) {
      console.error(e);
    }
  }

  public async graphQL(query: string, params: Record<string, any>) {
    try {
      const convertedBody = JSON.stringify({ query, params, locale: this._locale });
      const pageUrl = new URL(this._baseURL.toString());
      pageUrl.pathname += `content/graphql`;
      const graphQLRequest = await fetch(pageUrl, {
        method: "POST",
        headers: {
          "x-api-key": `${process.env.AW_API_KEY}`,
        },
        body: convertedBody,
        cache: "no-cache",
        next: {
          tags: ["monitor"],
        },
      });
      const data = await graphQLRequest.json();
      return data;
    } catch (e) {
      console.error(e);
    }
  }

  public async createEnquiry(data: Record<string, any>) {
    try {
      const requestUrl = new URL(this._baseURL.toString());
      requestUrl.pathname += "user/create-enquiry";
      if (data.eventName === "webchat_inquiry") {
        const [language, region] = data.locale.split("-") || ["en"];
        requestUrl.searchParams.append("botLanguage", language);
      }
      console.log("createEnquiry requestUrl", requestUrl.href.toString());
      const response = await fetch(requestUrl, {
        method: "POST",
        body: JSON.stringify(data),
        headers: {
          "x-api-key": `${process.env.AW_API_KEY}`,
        },
      });
      const result = await response.json();
      // console.info("submitGuidedTool result", result);
      return result;
    } catch (e) {
      console.error(e);
    }
  }

  public async allCommunitiesList() {
    try {
      const assetUrl = new URL(this._baseURL.toString());
      assetUrl.pathname += "content/communities";
      assetUrl.searchParams.set("locale", this._locale);

      const contentfulDataRequest = await fetch(assetUrl.toString(), {
        method: "GET",
        headers: {
          "x-api-key": process.env.AW_API_KEY || "",
        },
        next: { revalidate: 3600 },
      });

      const data = await contentfulDataRequest.json();

      if (Object.keys(data).length === 0) {
        return;
      } else {
        return data?.communities;
      }
    } catch (e) {
      console.error("Fetch failed:", e);
    }
  }
}
